import React, { ReactElement, useState, useEffect, useRef } from 'react'
import styles from './Select.module.scss'
import ArrowDownIcon from 'svg/dropdown-arrow.svg'
import classNames from 'classnames'
import useIsMobile from 'hooks/useIsMobile'

export type SelectOption = {
  fixedLabel?: string
  mobileLabel?: string
  label: string
  value: string | number | readonly string[] | undefined
  [key: string]: any
}

type SelectProps<T extends SelectOption> = {
  value?: T
  options: ReadonlyArray<T>
  onChange?: (value: T) => void
  label?: string
  disabled?: boolean
  dataSection?: string
  placeholder?: string
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>
  useNativeSelect?: boolean
  customClassName?: string
}

function Select<T extends SelectOption>({
  disabled,
  value,
  label,
  onChange,
  dataSection,
  placeholder,
  options,
  Icon,
  useNativeSelect,
  customClassName = '',
}: SelectProps<T>): ReactElement<SelectProps<T>> {
  const isMobileScreen = useIsMobile()
  const [selectedValue, setSelectedValue] = useState<
    string | number | readonly string[] | undefined
  >(value?.value)
  const [isOpen, setIsOpen] = useState(false)
  const parentRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)

  const handleChange = (clickedValue) => {
    const optionSelected = options.find(({ value }) => value === clickedValue)
    if (optionSelected) {
      setSelectedValue(optionSelected.value)
      onChange?.(optionSelected)
    }
    setIsOpen(false)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (parentRef.current && !parentRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  const toggleDropdown = () => {
    if (!disabled) {
      const selectElement = selectRef.current
      if (selectElement) {
        const style = window.getComputedStyle(selectElement)
        // Only triggers this click if it is mobile
        const isVisible = style.display !== 'none'
        if (isVisible) {
          selectElement.focus()
        } else {
          setIsOpen((prevState) => !prevState)
        }
      }
    }
  }

  const getSelectLabel = () => {
    const selectedOption = options.find(({ value }) => value === selectedValue)

    if (!!selectedOption?.fixedLabel) {
      return selectedOption.fixedLabel
    }

    return isMobileScreen && selectedOption?.mobileLabel
      ? selectedOption?.mobileLabel
      : selectedOption?.label
  }

  const selectedLabel = getSelectLabel()

  useEffect(() => {
    if (value?.value) {
      setSelectedValue(value.value)
    }
  }, [value?.value])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div
      className={classNames(styles.selectWrapper, {
        [customClassName]: customClassName !== '',
      })}
      data-section={dataSection}
      ref={parentRef}
    >
      {label && <label>{label}</label>}
      <div
        className={classNames(styles.selectContainer, {
          [styles.selectContainerNoBottomBorder]: isOpen,
        })}
        onClick={toggleDropdown}
      >
        {Icon && <Icon />}
        <div
          className={classNames(styles.desktopSelectLabel, {
            [styles.desktopSelectLabelWithIcon]: Icon,
          })}
        >
          <span>{selectedLabel || placeholder || 'Select an option'}</span>
        </div>

        <select
          ref={selectRef}
          value={selectedValue}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          aria-label={label || 'Select dropdown'}
          className={classNames(styles.selectComponent, {
            [styles.selectComponentHidden]: !useNativeSelect,
          })}
        >
          <option value='' disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={`${option.label}_${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className={styles.selectIcon}>{<ArrowDownIcon />}</div>
      </div>

      {isOpen && (
        <div
          className={classNames(styles.optionsList, {
            [styles.optionsListHide]: useNativeSelect,
          })}
        >
          {options.map(({ label, value }) => (
            <div key={label} onClick={() => handleChange(value)}>
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Select
